import * as React from "react";
import { Portal } from "react-portal";
import {RichUtils, EditorState, DraftBlockType, Editor, CompositeDecorator, ContentBlock, ContentState} from "draft-js";
import { uniqBy } from "lodash";

import * as Color from "shared/util/Color";
import {DraftJSUtils} from "./utility";
import {
    editorStateFromHtml,
    styles,
    exporter,
    customBlockStyleDataKeys,
    htmlFromEditorState,
    CUSTOM_FONT_STRING,
    CUSTOM_FONT_WEIGHT_STRING
} from "./editorStateConverters";
import {
    DraftStyles,
    exportedInlineStyleTypes,
    FONT_METADATA,
    DEFAULT_FONT_VARIANT_LIGATURES,
    AnchorTarget
} from "./models";
import {FontFamily} from "shared/xp/models/widgets";
import {
    Alignment,
    ChangeSource, FontOption,
    FontOptions,
    TextEditorControls,
    TextEditorControlsProps,
    textEditorDefaults
} from "./TextEditorControls";
import {TextEditor} from "./TextEditor";
import Storage, {FavoriteFont, StorageProperties} from "shared/services/Storage";
import { OrderedSet } from "immutable";
import {getEntityRanges} from "draft-js-utils";
import {FontFaceVariants} from "../../fonts/WebFontsUtil";
import * as CharacterPropertiesViewControllerHelpers
    from "../../../../../editor/ui/experience/widgets/CharacterPropertiesViewControllerHelpers";
import * as WebFontUtil from "../../fonts/WebFontsUtil";
import {FontSelectorQueryResults} from
        "../../../../../editor/ui/experience/widgets/CharacterPropertiesViewControllerHelpers";

interface TextEnhancedWidgetProps {
    text: string;
    isSelected: boolean;
    isEmailMode: boolean;
    readOnly: boolean;
    isTransforming: boolean;
    onChange: Function;
    onSelect: (event: React.MouseEvent) => void;
    onDeselect: (hasText: boolean) => void;
    onReady: () => void;
    onShowNotification: (msg: string) => void;
    fonts: FontSelectorQueryResults;
    uuid: string;
    hasAction: boolean;
}

interface TextEnhancedWidgetState {
    rawText: string;
    editorState: EditorState;
    selectedLineHeight?: number;
    selectedAlignment?: Alignment;
    hasFakeHighlight: boolean;
    textDecorations: Array<DraftStyles>;
    fontWeight: string;
    fontStyle: string;
    fontSize: number;
    fontColor: Color.RGBA;
    fontStyleOptions: Array<FontFaceVariants>;
    fontFamilyOptions: FontOptions[];
    fontFamilyData: FontFamily;
    isReady: boolean;
    anchorUrl: string;
    anchorTarget: AnchorTarget;
    hasSelection: boolean;
    selectionHasLink: boolean;
}

interface OnChangeParams {
    updatedEditorState: EditorState;
    forceChange?: boolean;
    blur?: boolean;
    targetFontOption?: FontOption;
}

type inlineStyles = Pick<TextEnhancedWidgetState,
    "fontColor" | "fontFamilyData" | "fontSize" | "fontStyle" | "fontWeight">;

const enum FontStyles {
    NORMAL = "normal",
    ITALIC = "italic",
}

// Valid font-family values can only contain letters and spaces unless they are quoted.
// For reference https://developer.mozilla.org/en-US/docs/Web/CSS/font-family and search for the heading
// "Valid family names".  So we are quoting font families, normalizing names and trimming from unnecessary whitespaces.
// Example: input font family name "Lucida Sans Unicode, Lucida Grande,sans-serif"
// output: ""lucida sans unicode","lucida grande","sans-serif""
const getNormalizedFontFamiliesName = (rawFontFamily: string): string => {
    const fontFamilies = rawFontFamily
        .replace(`"`, ``)
        .replace(`"`, ``)
        .toLowerCase()
        .split(",");
    let normalizedFontFamilies: string = "";
    for (let index = 0; index < fontFamilies.length; index++) {
        const familyName = fontFamilies[index];
        let doubleQuotedName: string = "";
        if(/^\s/.test(familyName)) {
            const whiteSpacesStartsWith = familyName.match(/\s+/);
            doubleQuotedName = doubleQuotedName.concat("\"", familyName.replace(whiteSpacesStartsWith[0], ``), "\"");
        } else {
            doubleQuotedName = doubleQuotedName.concat("\"", familyName, "\"");
        }

        if (index === 0) {
            normalizedFontFamilies = normalizedFontFamilies.concat(doubleQuotedName);
        } else {
            normalizedFontFamilies = normalizedFontFamilies.concat(",", doubleQuotedName);
        }
    }
    return normalizedFontFamilies;
};

const getSelectionFontFamily = (editorState: EditorState, fontFamily: string = "arial"): string => {
    const rawFontFamily = styles.fontFamily.current(editorState);
    // There is some strange behavior with fonts that have spaces in their family name. The string value contains
    // quotes like this '"Times New Roman"'
    return rawFontFamily && rawFontFamily.length ?
        rawFontFamily.split(`"`).join(``) : fontFamily;
};

const getFontFamilyOption = (targetFontFamily: string, fontFamilyOptions: FontOptions[]): FontOption => {
    const normalizedTargetFontFamily = getNormalizedFontFamiliesName(targetFontFamily);
    let targetFontOption = undefined;
    fontFamilyOptions.filter((item) => !!item).forEach(
        (fontGroup) => {
            if(!targetFontOption) {
                targetFontOption = fontGroup.options.find(
                    (fontOptionItem) => {
                        // We should also support already created experiences, with ETW inside,
                        // where fontFamilyName has been used instead of cssFamilyNames
                        const normalizedFontFamily = getNormalizedFontFamiliesName(fontOptionItem.value);
                        const normalizedCssFamily =
                            getNormalizedFontFamiliesName(fontOptionItem.fontDescriptor.get("cssFamily"));
                        return (normalizedCssFamily === normalizedTargetFontFamily)
                        || (normalizedFontFamily === normalizedTargetFontFamily);
                    }
                );
            }
        }
    );

    return targetFontOption;
};

const getFontFamilyData = (selectedFontOption: FontOption, fontStyle: string, fontWeight: string): FontFamily => {
    return DraftJSUtils.getFontFamilyProviderMetaData(selectedFontOption, fontStyle, fontWeight);
};

const findFontDescriptorData = (fontFamily: string): Promise<CharacterPropertiesViewControllerHelpers.FontProviderDescriptor> => new Promise((resolve) => {
    CharacterPropertiesViewControllerHelpers._fontSelectorQuery({
        term: fontFamily,
        callback: (fontResults) => {
            let currentOption = null;
            const fontResultsLength = fontResults.results.length;
            for(let i = 0; i < fontResultsLength; i++) {
                const currentFontFamilyOption = fontResults.results[i].children.find(
                    (fontProviderItem) =>
                        fontProviderItem.text.toLocaleLowerCase() === fontFamily.toLocaleLowerCase()
                );
                if (currentFontFamilyOption) {
                    currentOption = currentFontFamilyOption;
                    break;
                }
            }
            resolve(currentOption);
        }
    });
});

const findFontsDescriptors = (fontFamilies: string[]): Promise<CharacterPropertiesViewControllerHelpers.FontProviderDescriptor[]> => {
    return Promise.all(fontFamilies.map(findFontDescriptorData));
};

function findLinkEntities(contentBlock: ContentBlock, callback: (start: number, end: number) => void, contentState: ContentState) {
    contentBlock.findEntityRanges(
        (character) => {
            const entityKey = character.getEntity();
            if (entityKey !== null) {
                const entity = contentState.getEntity(entityKey)
                return entity.getType() === FONT_METADATA && !!entity.getData().url
            }
            return false
        },
        callback
    );
}

const linkDecorator = new CompositeDecorator([
    {
        strategy: findLinkEntities,
        component: (props) => {
        const {url, target} = props.contentState.getEntity(props.entityKey).getData();
        return (
            <a href={url} target={target} className="zmgs-anchor" style={styles.link} rel="noopener noreferrer">
                {props.children}
            </a>
        )},
    },
]);

// This is used with require syntax so usage is not detected.
// noinspection JSUnusedGlobalSymbols
class TextEnhancedWidget extends React.Component<TextEnhancedWidgetProps, TextEnhancedWidgetState> {
    constructor(props: TextEnhancedWidgetProps) {
        super(props);

        this.state = {
            rawText: null,
            editorState: null,
            hasFakeHighlight: false,
            textDecorations: null,
            fontWeight: null,
            fontStyle: null,
            fontSize: null,
            fontColor: null,
            fontStyleOptions: null,
            fontFamilyOptions: null,
            fontFamilyData: null,
            isReady: false,
            anchorUrl: "",
            anchorTarget: AnchorTarget.self,
            hasSelection: false,
            selectionHasLink: false
        };
    }

    editorRef: Editor;

    setRef = (ref: Editor) => {
        this.editorRef = ref;
    }

    componentDidUpdate(
        prevProps: Readonly<TextEnhancedWidgetProps>,
        prevState: Readonly<TextEnhancedWidgetState>
    ): void {
        const {isSelected, text, isEmailMode} = this.props;
        const {editorState, rawText} = this.state;
        if(isSelected && editorState && prevProps.isSelected !== isSelected && !editorState.getCurrentContent().hasText()) {
            this.setState({
                editorState: DraftJSUtils.forceFocusBeginning(this.state.editorState),
            });
        }
        if(!isSelected && prevProps.isSelected === true) {
            this.handleDeselect();
        }

        // Update widget state a if raw text state has been changed outside
        if(text && text.length !== 0 && !this.props.isSelected && text !== rawText) {  
            this.calculateState();
        }

        if(!prevState.isReady && this.state.isReady) {
            this.props.onReady();
        }

        if(isEmailMode && isEmailMode !== prevProps.isEmailMode){
            // need to apply all styles before resetting
            this.calculateState().then(
                () => this.onAnchorAttrsReset()
            );
        }
    }

    deriveStyleOverrides =
        ({fontSize, fontColor, fontFamilyData, fontStyle, fontWeight}: inlineStyles): OrderedSet<string> => {
            let dummyEditorState = EditorState.createEmpty();
            const {cssFamilyNames} = fontFamilyData;
            const parsedFontSize = `${fontSize}px`;
            dummyEditorState = styles.fontSize.add(dummyEditorState, parsedFontSize);
            dummyEditorState = styles.color.add(dummyEditorState, Color.formatRgbaToStyle(fontColor));
            dummyEditorState = styles.fontWeight.add(dummyEditorState, fontWeight);
            // We have to manually set fontVariantLigatures to 'none' value
            // for browser correctly handling font-variant
            dummyEditorState = styles.fontVariantLigatures.add(dummyEditorState, DEFAULT_FONT_VARIANT_LIGATURES);
            if(fontStyle === FontStyles.ITALIC) {
                dummyEditorState = RichUtils.toggleInlineStyle(dummyEditorState, DraftStyles.ITALIC);
            }
            dummyEditorState = styles.fontFamily
                .add(dummyEditorState, `${getNormalizedFontFamiliesName(cssFamilyNames)}`);

            return dummyEditorState.getCurrentInlineStyle();
        }

    applyDraftStyle = (style: DraftStyles, editorState: EditorState): EditorState =>
        DraftJSUtils.applyInlineStyle(editorState, style)

    removeDraftStyle = (style: DraftStyles, editorState: EditorState): EditorState =>
        DraftJSUtils.removeInlineStyle(editorState, style)

    // TODO: Add support for a selection range.
    getSelectionFontWeight = (editorState: EditorState): string => {
        return styles.fontWeight.current(editorState) || this.state.fontWeight;
    }

    // TODO: Add support for a selection range.
    getSelectionFontStyle = (editorState: EditorState): string => {
        const hasText = editorState.getCurrentContent().hasText();
        let targetFontStyle = this.state.fontStyle;
        if (hasText) {
            const currentStyle = editorState.getCurrentInlineStyle();
            targetFontStyle = currentStyle.has(DraftStyles.ITALIC) ? FontStyles.ITALIC : FontStyles.NORMAL;
        }
        return targetFontStyle;
    }

    getUsedFonts = (editorState: EditorState): string[] => {
        const currentContent = editorState.getCurrentContent();
        const editorStateStyles = exporter(editorState);
        const entities = [];
        currentContent.getBlockMap().forEach((block) => {
            const charMetaList = block.getCharacterList();
            const entityPieces = getEntityRanges(block.getText(), charMetaList);

            entityPieces.map(([entityKey, stylePieces]) => {
                const entity = entityKey ? currentContent.getEntity(entityKey) : undefined;
                const entityType = entity ? entity.getType() : undefined;

                if (entityType === FONT_METADATA) {
                    const font = entity.getData();
                    if (font.fontFamily) {
                        entities.push(font.fontFamily);
                    } else if (font.url) {
                        stylePieces.forEach(([_text, styleList]) => {
                            const fontFamilyStyle = styleList
                                .toJS()
                                .find((entityStyle: string) => entityStyle.startsWith(CUSTOM_FONT_STRING));

                            if (fontFamilyStyle && editorStateStyles[fontFamilyStyle]) {
                                const fontFamily = editorStateStyles[fontFamilyStyle].style.fontFamily.split(",")[0];
                                entities.push(fontFamily);
                            }
                        });
                    }

                }
            });
        });

        return entities;
    }

    getAllFontsAndMetadata = (editorState: EditorState): FontFamily[] => {
        const {fontFamilyOptions} = this.state;
        const editorStateStyles = exporter(editorState);
        const currentFont = fontFamilyOptions.find(({label}) => label === "current");
        const currentContent = editorState.getCurrentContent();
        let entities = [];
        currentContent.getBlockMap().forEach((block) => {
            let charMetaList = block.getCharacterList();
            let entityPieces = getEntityRanges(block.getText(), charMetaList);
            entityPieces.map(([entityKey, stylePieces]) => {
                let entity = entityKey ? currentContent.getEntity(entityKey) : null;
                let entityType = entity == null ? null : entity.getType();
                if (entityType === FONT_METADATA) {
                    const font = entity.getData();
                    if (font.fontFamily) {
                        entities.push(DraftJSUtils.getFontFamilyFromFontMetadataEntity(font));
                    } else if (font.url) {
                        stylePieces.forEach(([_text, styleListData]) => {
                            const styleList = styleListData.toJS();
                            const fontFamilyStyle = styleList.find((entityStyle: string) => entityStyle.startsWith(CUSTOM_FONT_STRING));
                            const fontWeightStyle = styleList.find((entityStyle: string) => entityStyle.startsWith(CUSTOM_FONT_WEIGHT_STRING));
                            const fontStyleItalic = styleList.find((entityStyle: string) => entityStyle.toLocaleLowerCase() === "italic");
                            const fontFamilyValue = editorStateStyles[fontFamilyStyle] && editorStateStyles[fontFamilyStyle].style.fontFamily.split(",")[0];
                            const fontOption = fontFamilyValue && currentFont.options.find(({value}) => value.toLowerCase() === fontFamilyValue.toLowerCase());
                            const fontStyle = fontStyleItalic ? "italic" : "normal";
                            const fontWeight = editorStateStyles[fontWeightStyle] && editorStateStyles[fontWeightStyle].style.fontWeight;
                            if (fontOption && fontWeight) {
                                const font = getFontFamilyData(fontOption, fontStyle,fontWeight);
                                entities.push(DraftJSUtils.getFontFamilyFromFontMetadataEntity(font));
                            }
                        });
                    }
                }
            });
        });

        return entities;
    }

    getUniqueFonts = (editorState: EditorState): string[] => {
        return this.getUsedFonts(editorState)
            .filter((value, index, self) => self.findIndex(item => item === value) === index)
    }

    onToggleFontFamily = (targetFontFamily: string) => {
        const {fontWeight, fontStyle, editorState} = this.state;
        const hasSelectedText = this.hasSelectedText();
        let updatedEditorState = !hasSelectedText
            ? DraftJSUtils.forceSelectAll(this.state.editorState)
            : editorState;
        const targetFontOption = getFontFamilyOption(targetFontFamily, this.state.fontFamilyOptions);
        const fontStyleOptions = WebFontUtil.variantsFromFontDescriptor(targetFontOption.fontDescriptor);
        const currentFontStyleOption = fontStyleOptions.find(
            ({ weight, style }) => weight === fontWeight && style === fontStyle
        ) || fontStyleOptions[0];
        const fontFamilyData: FontFamily = DraftJSUtils.getFontFamilyProviderMetaData(
            targetFontOption, currentFontStyleOption.style, currentFontStyleOption.weight
        );

        const rawFontFamily = fontFamilyData.cssFamilyNames ? fontFamilyData.cssFamilyNames : fontFamilyData.fontFamily;
        const normalizedFontFamily = getNormalizedFontFamiliesName(rawFontFamily);

        updatedEditorState = styles.fontFamily.add(updatedEditorState, `${normalizedFontFamily}`);
        Storage.Local.update(Storage.Keys.LAST_USED_TEXT_PROPERTIES, "fontFamily", fontFamilyData.fontFamily);

        if (fontFamilyData) {
            updatedEditorState = DraftJSUtils.setFontMetaData(updatedEditorState, fontFamilyData);

            let favoriteFonts: FavoriteFont[] = Storage.Local.get(Storage.Keys.EDITOR, "favoriteFonts", []);

            // Removing the currently selected font from the list.
            favoriteFonts = favoriteFonts.filter((item) => item.fontFamily !== fontFamilyData.fontFamily);

            // Put the newly selected font towards the beginning.
            favoriteFonts.unshift({
                fontProvider: fontFamilyData.fontProvider,
                fontFamily: fontFamilyData.fontFamily
            });

            // Leaving only 5 elements
            favoriteFonts = favoriteFonts.slice(0, 5);
            Storage.Local.update(Storage.Keys.EDITOR, "favoriteFonts", favoriteFonts);

            // update style options for a new font
            updatedEditorState = this.applyFontStyleOptions({
                editorState: updatedEditorState,
                fontStyle: fontFamilyData.fontStyle,
                fontWeight: fontFamilyData.fontWeight
            });

            this.handleFontFamilyInputChange("");

            this.setState({
                fontFamilyData,
                fontStyleOptions,
                fontWeight: fontFamilyData.fontWeight,
                fontStyle: fontFamilyData.fontStyle
            }, () => this.onChange({ updatedEditorState, blur: !hasSelectedText, targetFontOption }));
        }
        if (!hasSelectedText) {
            updatedEditorState = DraftJSUtils.forceUnselectAll(updatedEditorState);
        }
    }

    toggleColor = (color: Color.RGBA) => {
        const cssFormatted = `rgb(${color.r},${color.g},${color.b})`;
        const updatedEditorState = styles.color.add(this.state.editorState, cssFormatted);

        Storage.Local.update(Storage.Keys.LAST_USED_TEXT_PROPERTIES, "color", Color.formatRgbaToHex(color));

        return this.onChange({ updatedEditorState });
    }

    applyFontStyleOptions = ({editorState, fontStyle, fontWeight}: Pick<TextEnhancedWidgetState, "editorState" | "fontStyle" | "fontWeight">) => {
        const {fontWeight: currentFontWeight, fontStyle: currentFontStyle} = this.state;

        if (this.hasSelectedText()) {
            if(currentFontWeight !== fontWeight) {
                // We might need to remove the existing font weight
                editorState = styles.fontWeight.add(editorState, fontWeight);
                Storage.Local.update(Storage.Keys.LAST_USED_TEXT_PROPERTIES, "fontWeight", fontWeight);
            }

            if(currentFontStyle !== fontStyle) {
                editorState = RichUtils.toggleInlineStyle(editorState, DraftStyles.ITALIC);
                Storage.Local.update(Storage.Keys.LAST_USED_TEXT_PROPERTIES, "fontStyle", fontStyle);
            }
        } else {
            editorState = styles.fontWeight.add(editorState, fontWeight);
            Storage.Local.update(Storage.Keys.LAST_USED_TEXT_PROPERTIES, "fontWeight", fontWeight);

            editorState = fontStyle === "italic"
                ? this.applyDraftStyle(DraftStyles.ITALIC, editorState)
                : this.removeDraftStyle(DraftStyles.ITALIC, editorState)
            Storage.Local.update(Storage.Keys.LAST_USED_TEXT_PROPERTIES, "fontStyle", fontStyle);
        }

        return editorState;
    }

    toggleFontStyle = (fontStyle: string, fontWeight: string) => {
        // what logic should be if font doesn't have font-style?
        const { editorState, fontFamilyData: currentFontFamilyData } = this.state;
        const hasSelectedText = this.hasSelectedText();
        let shouldCallOnChange = true;

        let updatedEditorState = !hasSelectedText
            ? DraftJSUtils.forceSelectAll(editorState)
            : editorState;

        updatedEditorState = this.applyFontStyleOptions({
            editorState: updatedEditorState,
            fontStyle,
            fontWeight
        });

        const targetFontOption = this.state.fontFamilyOptions.find(item => item.label === "current");

        if (targetFontOption) {
            const currentFontOption = targetFontOption.options.find(
                ({value}) => value.toLowerCase() === currentFontFamilyData.fontFamily.toLowerCase()
            );
            const fontFamilyData: FontFamily = getFontFamilyData(
                currentFontOption, fontStyle, fontWeight
            );

            if (fontFamilyData) {
                updatedEditorState = DraftJSUtils.setFontMetaData(updatedEditorState, fontFamilyData);
                this.setState({
                    fontFamilyData,
                    fontWeight: fontFamilyData.fontWeight,
                    fontStyle: fontFamilyData.fontStyle
                }, () => this.onChange({ updatedEditorState, blur: !hasSelectedText }));
                shouldCallOnChange = false;
            }
        }

        if (!hasSelectedText) {
            updatedEditorState = DraftJSUtils.forceUnselectAll(updatedEditorState);
        }

        if (shouldCallOnChange) {
            this.onChange({ updatedEditorState, blur: !hasSelectedText });
        }
    }

    toggleLetterSpacing = (letterSpacing: string) => {
        const parsedLetterSpacing = letterSpacing.indexOf("px") === -1 ? `${letterSpacing}px` : letterSpacing;
        const updatedEditorState = styles.letterSpacing.add(this.state.editorState, parsedLetterSpacing);

        return this.onChange({ updatedEditorState });
    }

    toggleFontSize = (fontSize: number) => {
        const updatedEditorState = styles.fontSize.add(this.state.editorState, `${fontSize}px`);
        Storage.Local.update(Storage.Keys.LAST_USED_TEXT_PROPERTIES, "fontSize", fontSize);

        return this.onChange({ updatedEditorState });
    }

    toggleBlockType = (draftBlockType: DraftBlockType) => {
        const updatedEditorState = RichUtils.toggleBlockType(this.state.editorState, draftBlockType);

        return this.onChange({ updatedEditorState });
    }

    toggleAlign = (alignment: string) => {
        const updatedEditorState = DraftJSUtils.mergeBlockData(
            this.state.editorState,
            {
                [customBlockStyleDataKeys.textAlign]: alignment,
            }
        );
        Storage.Local.update(Storage.Keys.LAST_USED_TEXT_PROPERTIES, "textAlign", alignment);
        this.onChange({ updatedEditorState });
    }

    toggleLineHeight = (lineHeight: string) => {
        const updatedEditorState = DraftJSUtils.mergeBlockData(
            this.state.editorState,
            {
                [customBlockStyleDataKeys.lineHeight]: lineHeight,
            }
        );

        this.onChange({ updatedEditorState });
    }

    handleFocus = () => {
        if(this.state.hasFakeHighlight) {
            this.setState({hasFakeHighlight: false});
        }
    }

    hasSelectedText = () => DraftJSUtils.getSelectionText(this.state.editorState);

    handleDeselect = () => {
        this.handleFontFamilyInputChange("");
        const hasText = this.state.editorState.getCurrentContent().hasText();
        // we need to unselect all editorState, because when we select ETW next time via drag-to-select,
        // editorState will keep selected text (before unselecting) without highlighting it
        this.setState(
            ({ editorState }) => ({ editorState: DraftJSUtils.forceUnselectAll(editorState) }),
            () => this.editorRef.blur()
        );
        this.props.onDeselect(hasText);
    }

    handleDragContainerMouseDown = (event: React.MouseEvent<HTMLElement>) => {
        this.props.onSelect(event);
    }

    handleMouseDown = () => {
        this.handleFocus();
        const {editorState, hasFakeHighlight} = this.state;

        // Unfortunately, react-js editor won't fire onChange event
        // at the click in already selected block. So this resets
        // current selection, and call onChange for apply it.
        // Same time, we don't have to reset selection, in case it has fake highlight
        if(!hasFakeHighlight) {
            this.onChange({ updatedEditorState: DraftJSUtils.forceFocusBeginning(editorState) });
        }
    }

    handleMouseUp = (event: React.MouseEvent<HTMLElement>) => {
        const {editorState} = this.state;
        const eventTarget = (event.target as HTMLElement);
        const hasFocus = eventTarget.tagName.toLowerCase() === "span";

        if (!hasFocus) {
            if (this.props.isTransforming) {
                this.onChange({ updatedEditorState: DraftJSUtils.forcePreviousFocus(editorState)});
            } else {
                this.onChange({ updatedEditorState: DraftJSUtils.forceFocusEnding(editorState)});
            }
        }
    }

    getLinkState = (editorState: EditorState) => {
        const contentState = editorState.getCurrentContent();
        const selectionState = editorState.getSelection();

        // no text means no link data
        if (!contentState.hasText()) return {
            anchorUrl: "",
            anchorTarget: AnchorTarget.self,
            hasSelection: false,
            selectionHasLink: false
        };

        // identify the start and end points within the selected blocks/paragraphs
        const selectionStartBlockKey = selectionState.getStartKey();
        const selectionEndBlockKey = selectionState.getEndKey();
        const selectionStartOffset = selectionState.getStartOffset();
        const selectionEndOffset = selectionState.getEndOffset();

        const linkEntities = []
        let currentBlockKey = selectionStartBlockKey;

        // iterate through selected blocks/paragraphs to find links
        while (currentBlockKey) {
            const contentBlock = contentState.getBlockForKey(currentBlockKey);
            contentBlock.findEntityRanges(
                (character) => {
                    const charEntityKey = character.getEntity();
                    if (charEntityKey) {
                        const entity = contentState.getEntity(charEntityKey)
                        return entity.getType() === FONT_METADATA && !!entity.getData().url
                    }
                    return false
                },
                (start, end) => {
                    // filter for link entities overlapping with the selection
                    if (!(currentBlockKey === selectionStartBlockKey && end < selectionStartOffset) &&
                        !(currentBlockKey === selectionEndBlockKey && start > selectionEndOffset)) {
                            linkEntities.push(contentBlock.getEntityAt(start));
                    }
                }
            );
            currentBlockKey = currentBlockKey === selectionEndBlockKey ? null : contentState.getKeyAfter(currentBlockKey);
        }

        // if there are no existing links, use the defaults
        // if there are one or more existing links, load the data from the first link
        const selectionHasLink = linkEntities.length > 0;
        const { url: anchorUrl = "", target: anchorTarget = AnchorTarget.self } = selectionHasLink ? contentState.getEntity(linkEntities[0]).getData() : {};
        return {
            anchorUrl,
            anchorTarget,
            hasSelection: !selectionState.isCollapsed(),
            selectionHasLink
        };
    }

    onChange = ({ updatedEditorState, forceChange = false, blur = false, targetFontOption = null }: OnChangeParams) => {
        let { fontFamilyOptions } = this.state;
        const currentStyle = updatedEditorState.getCurrentInlineStyle();
        const textDecorations = exportedInlineStyleTypes
            .filter((inlineStyleTypeItem) => currentStyle.has(inlineStyleTypeItem.draftStyle))
            .map((inlineStyleTypeItem) => inlineStyleTypeItem.draftStyle);
        const fontWeight = this.getSelectionFontWeight(updatedEditorState);
        const fontStyle = this.getSelectionFontStyle(updatedEditorState);
        const currentFontColor = styles.color.current(updatedEditorState);
        const fontColor = !!currentFontColor ? Color.parseRgba(currentFontColor) : this.state.fontColor;
        const fontFamily = getSelectionFontFamily(updatedEditorState, this.state.fontFamilyData.fontFamily);
        const selectedFontOption = getFontFamilyOption(fontFamily, this.state.fontFamilyOptions);
        const fontFamilyData = getFontFamilyData(selectedFontOption, fontStyle, fontWeight);
        const fontStyleOptions = WebFontUtil.variantsFromFontDescriptor(selectedFontOption.fontDescriptor);
        const linkState = this.getLinkState(updatedEditorState);
        const currentFontFamilyOption = {
            label: "current",
            options: [selectedFontOption]
        };

        let currentFontFamilyOptions = fontFamilyOptions.find(({label}) => label === "current").options;
        const uniqueFontFamilies = this.getUniqueFonts(updatedEditorState);

        // in the case when fontFamily was changed, we must add new fontFamily to current font options
        if(targetFontOption) {
            if(uniqueFontFamilies.length > 0) {
                currentFontFamilyOptions.push(targetFontOption);
            } else {
                currentFontFamilyOptions = [targetFontOption];
            }
        }

        const updatedCurrentFontFamilyOption = {
            label: "current",
            options: uniqueFontFamilies.length > 0
                ? currentFontFamilyOptions.filter(
                    ({value}) => uniqueFontFamilies.find(item => item.toLocaleLowerCase() === value.toLowerCase())
                )
                // when ETW is empty, take only first font option from current fonts
                : [currentFontFamilyOptions[0]]
        };
        // we need to update "current" fontFamilyOption and its style options due to caret position
        fontFamilyOptions = fontFamilyOptions.filter((item) => !!item).map(
            (option) => option.label === "current" ? updatedCurrentFontFamilyOption : option);

        if (!fontFamilyOptions.find(option => option.label === "current")) {
            fontFamilyOptions = [currentFontFamilyOption, ...fontFamilyOptions];
        }

        const fontFamilies = this.getAllFontsAndMetadata(updatedEditorState);
        const fontSizeStyle = styles.fontSize.current(updatedEditorState);
        const fontSizeValue =
            fontSizeStyle && fontSizeStyle.indexOf("px") ? fontSizeStyle.slice(0, -2) : fontSizeStyle;
        const fontSizeNumber = parseInt(fontSizeValue, 10);
        const fontSize = isNaN(fontSizeNumber) ? this.state.fontSize : fontSizeNumber;

        const currentContent = this.state.editorState.getCurrentContent();
        const hasMaterialChange = currentContent !== updatedEditorState.getCurrentContent();

        const selectedAlignments = DraftJSUtils.getSelectionStylePropertyValues(
            updatedEditorState,
            customBlockStyleDataKeys.textAlign);

        const selectedLineHeights = DraftJSUtils.getSelectionStylePropertyValues(
            updatedEditorState,
            customBlockStyleDataKeys.lineHeight);
        const rawText = currentContent.getPlainText();
        let currentHtml = this.state.rawText;
        // TODO: Improve change detection. Specifically filter out the HIGHLIGHT style change.
        if(hasMaterialChange || forceChange) {
            // Only emit html change when we have a change in content
            currentHtml = htmlFromEditorState(updatedEditorState);

            // Emit the exported HTML and raw text for use in labels
            this.props.onChange(currentHtml, fontFamilies, rawText);
        }

        this.setState({
            fontWeight,
            fontStyle,
            fontColor,
            fontSize,
            editorState: updatedEditorState,
            selectedLineHeight: selectedLineHeights[0],
            selectedAlignment: selectedAlignments[0],
            textDecorations,
            rawText: currentHtml,
            fontFamilyData,
            fontFamilyOptions,
            fontStyleOptions,
            ...linkState
        }, () => {
            if (blur) {
                this.editorRef.blur();
            }
        });
    }

    handleToolboxMouseDown = () => {
        if(!this.state.hasFakeHighlight) {
            this.setState({hasFakeHighlight: true});
        }
    }

    updateDraftStyles = (textDecorators: Array<DraftStyles>) => {
        const {textDecorations: previousTextDecorators} = this.state;

        let updatedEditorState = this.state.editorState;
        // Removing each style one by one then reapplying. May be able to do this in batch
        previousTextDecorators.forEach((style) =>
            updatedEditorState = this.removeDraftStyle(style, updatedEditorState));
        textDecorators.forEach((style) => updatedEditorState = this.applyDraftStyle(style, updatedEditorState));

        this.onChange({ updatedEditorState });
    }

    ControlChangeHandler = (propsDelta: Partial<TextEditorControlsProps>, changeSource: ChangeSource) => {
        switch(changeSource) {
            case ChangeSource.Alignment:
                this.toggleAlign(propsDelta.alignment);
                break;
            case ChangeSource.BlockType:
                this.toggleBlockType(propsDelta.blockType);
                break;
            case ChangeSource.Color:
                this.toggleColor(propsDelta.fontColor);
                break;
            case ChangeSource.Decoration:
                this.updateDraftStyles(propsDelta.textDecorations);
                break;
            case ChangeSource.FontSize:
                this.toggleFontSize(propsDelta.fontSize);
                break;
            case ChangeSource.FontStyle:
                this.toggleFontStyle(propsDelta.fontStyle, propsDelta.fontWeight);
                break;
            case ChangeSource.LetterSpacing:
                this.toggleLetterSpacing(propsDelta.letterSpacing.toString(10));
                break;
            case ChangeSource.LineHeight:
                this.toggleLineHeight(propsDelta.lineHeight.toString(10));
                break;
            default:
                break;
        }
    }

    handleFontFamilyInputChange = (term: string) => {
        CharacterPropertiesViewControllerHelpers._fontSelectorQuery({
            term,
            callback: (fonts) => {
                const fontFamilyOptions = fonts.results.map<FontOptions>((provider) => ({
                    label: provider.text,
                    options: provider.children.map(
                        (fontProvider) => ({
                            label: fontProvider.text,
                            value: fontProvider.text,
                            fontDescriptor: fontProvider.fontDescriptor
                        })
                    ),
                }));

                const currentFontFamilyOption = this.state.fontFamilyOptions.filter((item) => !!item).find(({label}) => label === "current");
                if (currentFontFamilyOption) {
                    const nextFontFamilyOptions = [ currentFontFamilyOption, ...fontFamilyOptions ].filter((item) => !!item);
                    this.setState({fontFamilyOptions: [...nextFontFamilyOptions]});
                }
            }
        });
    }

    calculateState = async () => {
        const { text, fonts } = this.props;
        let updatedEditorState;

        const lastUsed: StorageProperties = Storage.Local.get(Storage.Keys.LAST_USED_TEXT_PROPERTIES);
        const {
            color = textEditorDefaults.fontColor,
            fontSize = textEditorDefaults.fontSize,
            fontWeight = textEditorDefaults.fontWeight,
            fontStyle = textEditorDefaults.fontStyle,
            textAlign = textEditorDefaults.alignment,
            fontFamily
        } = lastUsed;

        if(!text || text.length === 0) {
            updatedEditorState = EditorState.createEmpty();
            updatedEditorState = DraftJSUtils.setDefaultBlockStyles({ editorState: updatedEditorState, textAlign });
        } else {
            const {contentState} = editorStateFromHtml(text);
            updatedEditorState = EditorState.createWithContent(contentState, linkDecorator);
        }

        const fontColor = typeof color === "string" ? Color.parseRgba(Color.hexToRgba(color, "1")) : color;

        let fontFamilyOptions = fonts.results.map((provider) => ({
            label: provider.text,
            options: provider.children.map(
                (fontProviderItem): FontOption => ({
                    label: fontProviderItem.text,
                    value: fontProviderItem.text,
                    fontDescriptor: fontProviderItem.fontDescriptor,
                })
            )
        })).filter((item) => !!item);

        // Try to use last used <fontFamily> if exists
        const currentFontFamily = getSelectionFontFamily(updatedEditorState, fontFamily);

        const inlineStyle = updatedEditorState.getCurrentInlineStyle();

        const isUnformattedText = updatedEditorState.getCurrentContent().hasText() && inlineStyle.size === 0;

        if (isUnformattedText) {
            updatedEditorState = DraftJSUtils.applyDefaultStyles({
                editorState: updatedEditorState,
                fontSize,
                fontColor,
                fontWeight,
                currentFontFamily
            });
        }
        const uniqueFonts = this.getUniqueFonts(updatedEditorState);
        const usedFonts = uniqueFonts.length > 0 ? uniqueFonts : [currentFontFamily];
        const formattedFonts = usedFonts.map(font => font.split(',')[0].replace(/\"/g, ''))
        const fontsDescriptors = await findFontsDescriptors(formattedFonts);
            const currentFontFamilyOptions = [{
                label: "current",
                options: fontsDescriptors.map(
                    (option) => ({
                        label: option.text,
                        value: option.text,
                        fontDescriptor: option.fontDescriptor
                    })
                ),
            }];

        if (currentFontFamilyOptions) {
            fontFamilyOptions = [...currentFontFamilyOptions, ...fontFamilyOptions];
        }

        const selectedFontOption = getFontFamilyOption(currentFontFamily, fontFamilyOptions);
        const fontStyleOptions = selectedFontOption
            ? WebFontUtil.variantsFromFontDescriptor(selectedFontOption.fontDescriptor)
            : [];

        const fontFamilyData = getFontFamilyData(selectedFontOption, fontStyle, fontWeight);
        const selectedLineHeights = DraftJSUtils.getSelectionStylePropertyValues(
            updatedEditorState,
            customBlockStyleDataKeys.lineHeight);
        const currentLineHeight = selectedLineHeights && selectedLineHeights.length > 0
                ? selectedLineHeights[0]
                : textEditorDefaults.lineHeight;
        const hasFakeHighlight = this.state.isReady ? this.state.hasFakeHighlight : false;

        if (isUnformattedText) {
            // This is first iteration of calculating state for newly created ETW
            // In case it's not standalone ETW, but used in DPW for example
            // So it has <text> value, but it has no selection
            // For apply initial fontProvider Entity, we have to apply it to not empty selection
            // So this selection should contain the <text> value selected
            const selection = updatedEditorState.getSelection();
            // 1) Select value <text>
            updatedEditorState = DraftJSUtils.forceSelectAll(updatedEditorState);
            // 2) Apply fontProvider Entity
            updatedEditorState = DraftJSUtils.setFontMetaData(updatedEditorState, fontFamilyData);
            // 3) Revert back initial selection and focus cursor
            updatedEditorState = EditorState.forceSelection(updatedEditorState, selection);
        }

        this.setState({
            editorState: updatedEditorState,
            rawText: text,
            // Setting default values for our state, we'll respond to cursor location change or toolbox change.
            fontWeight,
            fontStyle,
            fontColor,
            fontSize,
            selectedAlignment: Alignment.LEFT,
            selectedLineHeight: currentLineHeight,
            hasFakeHighlight,
            textDecorations: [],
            fontStyleOptions,
            fontFamilyOptions,
            fontFamilyData,
            isReady: true
        },            () => {
            if (isUnformattedText) {
                this.onChange({updatedEditorState, forceChange: true });
            }
        });
    }

    onTextEditorChange = (updatedEditorState: EditorState) => {
        this.onChange({ updatedEditorState });
    }

    handleAnchorUrlChange = (anchorUrl: string) => {
        this.setState({anchorUrl});
    }

    handleAnchorTargetChange = (anchorTarget: AnchorTarget) => {
        this.setState({anchorTarget});
    }

    onAnchorAttrsReset = () => {
        let {editorState} = this.state;
        editorState =  DraftJSUtils.forceSelectAll(editorState)
        const {selectionHasLink} = this.getLinkState(editorState)
        if(selectionHasLink){
            this.setState({ editorState: EditorState.forceSelection(DraftJSUtils.setFontMetaData(editorState, { url:'', target:'_self' }, linkDecorator), editorState.getSelection()) }, ()=>{
                this.props.onShowNotification('We have removed actions/animations that are not compatible with email mode from your widgets where applicable')
                this.onChange({updatedEditorState: this.state.editorState, forceChange: true})
            });
        }
    }

    onAnchorAttrsApply = (url: string, target: string) => {
        const {editorState} = this.state;

        this.setState({ editorState: EditorState.forceSelection(DraftJSUtils.setFontMetaData(editorState, { url, target }, linkDecorator), editorState.getSelection()) });
    }

    getAppliedUniqueFontStyles = (editorState: EditorState, fontOption: string) => {
        return uniqBy(this.getAllFontsAndMetadata(editorState), (fontItem) => fontItem[fontOption]);
    }

    componentDidMount() {
        this.calculateState().then(()=>{
            if(this.props.isEmailMode){
                this.onAnchorAttrsReset();
            }
        });
    }

    render() {
        // for the first render there is no needed data, because we need to accomplish async operation
        // (search for a "current" font)
        if(!this.state.isReady) {
            return null;
        }

        const {
            editorState,
            fontColor,
            fontSize,
            selectedLineHeight,
            selectedAlignment,
            textDecorations,
            hasFakeHighlight,
            fontFamilyOptions,
            anchorUrl,
            anchorTarget,
            hasSelection,
            selectionHasLink
        } = this.state;

        let {
            fontFamilyData,
            fontStyle,
            fontWeight,
            fontStyleOptions
        } = this.state

        const {
            isEmailMode,
            isSelected,
            readOnly,
            uuid,
            hasAction
        } = this.props;

        const styleOverrides = this.deriveStyleOverrides(
            {fontFamilyData, fontColor, fontSize, fontStyle, fontWeight});

        const currentBlockType = RichUtils.getCurrentBlockType(editorState);
        const currentLetterSpacing = styles.letterSpacing.current(editorState);
        const portalTarget = document && document.getElementById("character-properties");

        const moveContainerStyles: React.CSSProperties = {
            position: "absolute",
            top: "-10px",
            right: "-10px",
            bottom: "-10px",
            left: "-10px",
            zIndex: -1,
            cursor: "move",
        };

        if (!editorState.getSelection().get("hasFocus") && !this.hasSelectedText()) {
            const uniqueFontFamilies = this.getAppliedUniqueFontStyles(editorState, "fontFamily");

            if (uniqueFontFamilies.length > 1) {
                fontFamilyData = null;
                fontStyle = null;
                fontWeight = null;
                fontStyleOptions = [];
            } else {
                const uniqueFontWeights = this.getAppliedUniqueFontStyles(editorState, "fontWeight");
                const uniqueFontStyles = this.getAppliedUniqueFontStyles(editorState, "fontStyle");

                if (uniqueFontWeights.length > 1) {
                    fontWeight = null;
                }
                if (uniqueFontStyles.length > 1) {
                    fontStyle = null;
                }
            }
        }

        return (
            <>
                { isSelected && portalTarget &&
                <Portal node={portalTarget}>
                    <div onMouseDown={this.handleToolboxMouseDown}>
                        <TextEditorControls
                            onChange={this.ControlChangeHandler}
                            textDecorations={textDecorations}
                            alignment={selectedAlignment}
                            blockType={currentBlockType}
                            fontColor={fontColor}
                            fontSize={fontSize}
                            fontStyle={fontStyle}
                            fontWeight={fontWeight}
                            hasAction={hasAction}
                            letterSpacing={currentLetterSpacing}
                            lineHeight={selectedLineHeight}
                            onMouseDown={this.handleToolboxMouseDown}
                            fontStyleOptions={fontStyleOptions}
                            fontFamilyOptions={fontFamilyOptions}
                            fontProvider={fontFamilyData}
                            anchorUrl={anchorUrl}
                            anchorTarget={anchorTarget}
                            hasSelection={hasSelection}
                            selectionHasLink={selectionHasLink}
                            onFontFamilyInputChange={this.handleFontFamilyInputChange}
                            onToggleFontFamily={this.onToggleFontFamily}
                            onAnchorAttrsApply={this.onAnchorAttrsApply}
                            handleAnchorUrlChange={this.handleAnchorUrlChange}
                            handleAnchorTargetChange={this.handleAnchorTargetChange}
                            isEmailMode={isEmailMode}
                        />
                    </div>
                </Portal>
                }
                <div
                    onMouseDown={this.handleMouseDown}
                    onMouseUp={this.handleMouseUp}
                    className="editor-container"
                >
                    <TextEditor
                        readOnly={readOnly}
                        onEditorChange={this.onTextEditorChange}
                        editorState={editorState}
                        hasFakeHighlight={hasFakeHighlight}
                        isSelected={isSelected}
                        styleOverrides={styleOverrides}
                        fontProvider={fontFamilyData}
                        setRef={this.setRef}
                    />
                </div>
                <div
                    className={"drag-container"}
                    data-contained-widget-uuid={uuid}
                    style={moveContainerStyles}
                    onMouseDown={this.handleDragContainerMouseDown}
                />
            </>
        );
    }
}

// noinspection JSUnusedGlobalSymbols
export {
    TextEnhancedWidget as default,
    TextEnhancedWidget
};



// WEBPACK FOOTER //
// ./src/shared/ui/experience/widgets/textEnhancedWidget/TextEnhancedComponent.tsx